// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/Helpers/utils.js (origin/main-dev, 690 lines).
 * BYTE-IDENTICAL — this file produces the JSON that the Asterisk dialplan
 * consumes. Any change to `transformNodes` output shape or key order can
 * break call flow in production. If you must edit anything here, snapshot
 * the JSON before and after and diff byte-for-byte.
 *
 * Preserved verbatim: property write order in `flowJson`, dtmf handle
 * sorting, per-verb branches, flowControl `default | if | else | dtmf_*`
 * key shape, `promptDuration` computation, `ivr_category` toggle,
 * `getAcyclicLayoutEdges` DFS cycle-strip for dagre.
 */
import { DTMF_KEYS, ACTION_COLORS, MAX_CONDITIONS } from '@/utilities/constant';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { AGENT_CALL_CONNECT_LIST } from '@/service/url';
import { AI_TRANSFER_TARGET_TYPES } from '@/features/inbound-calls/AiAgentCompletionConfig';

export const aiCompletionHandleId = (nodeId: any, name: any) =>
  `${nodeId}_ct_${encodeURIComponent(name)}`;

// The "Counter" flow variable (flow_variables master table) only means
// something inside a Conditional node, so other nodes hide it. Matched by
// name/value since its id differs per environment.
export const isCounterFlowVariable = (name: any, value: any) =>
  /counter/i.test(`${name ?? ''} ${value ?? ''}`);

// Placeholder a flow variable is inserted as in TTS text.
export const flowVariableToken = (v: any) => `{${v?.value ?? ''}}`;

export const CONDITION_GROUP_OPERATORS = ['AND', 'OR'];

const generateConditionId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const emptyConditionRule = () => ({
  rowId: generateConditionId(),
  variableId: '',
  variableName: '',
  variableValue: '',
  operatorId: '',
  operatorLabel: '',
  operatorValue: '',
  variableType: '',
  value: '',
});

export const emptyConditionGroup = () => ({
  groupId: generateConditionId(),
  operator: 'AND',
  rules: [emptyConditionRule()],
});

// Conditional node shape: each group is one branch of an if / else-if chain,
// evaluated in order; rules inside a group are joined by the group's own
// `operator` (AND / OR). Flows saved before groups existed stored flat rows —
// each one becomes a single-rule group. Ids for upgraded data are derived from
// the position so they (and the handle ids built from them) stay stable
// across renders until the node is saved again.
export const normalizeConditionGroups = (conditions: any) =>
  (Array.isArray(conditions) ? conditions : []).map((c: any, i: number) =>
    Array.isArray(c?.rules)
      ? {
          groupId: c.groupId || `g${i}`,
          operator: c.operator === 'OR' ? 'OR' : 'AND',
          rules: c.rules.map((r: any, j: number) => ({
            ...r,
            rowId: r.rowId || `g${i}r${j}`,
          })),
        }
      : {
          groupId: `g${i}`,
          operator: 'AND',
          rules: [{ ...c, rowId: c?.rowId || `g${i}r0` }],
        },
  );

// One source handle per condition group, keyed by groupId so edges survive
// adding/removing other groups. `${nodeId}_if` is the pre-chain handle; it is
// treated as the first group's handle wherever edges are read.
export const conditionHandleId = (nodeId: any, groupId: any) =>
  `${nodeId}_cond_${groupId}`;
export const legacyConditionIfHandleId = (nodeId: any) => `${nodeId}_if`;
export const conditionElseHandleId = (nodeId: any) => `${nodeId}_else`;

export const conditionGroupHandleIds = (
  nodeId: any,
  groupId: any,
  index: number,
) =>
  index === 0
    ? [conditionHandleId(nodeId, groupId), legacyConditionIfHandleId(nodeId)]
    : [conditionHandleId(nodeId, groupId)];

// flowControl key / branch name: if, else_if_1, else_if_2, ... (else is separate).
export const conditionBranchKey = (index: number) =>
  index === 0 ? 'if' : `else_if_${index}`;
export const conditionBranchLabel = (index: number) =>
  index === 0 ? 'If' : `Else If ${index}`;

export const isConditionRuleComplete = (rule: any) =>
  !!rule?.variableId &&
  !!rule?.operatorId &&
  !!rule?.variableType &&
  rule?.value !== '' &&
  rule?.value !== undefined &&
  (rule.variableType !== 'Number' ||
    /^-?\d+(\.\d+)?$/.test(String(rule.value)));

export const AI_BRANCH_ALLOWED_TYPES =['hangupNode', 'playNode', 'transferNode'];
const AI_BRANCH_ERROR =
  'Only Hangup, Play or Transfer nodes can follow an AI Agent node';

const collectDownstreamIds = (startIds: any[], edges: any[]) => {
  const seen = new Set(startIds);
  const queue = [...startIds];
  while (queue.length) {
    const cur = queue.shift();
    edges.forEach((e: any) => {
      if (e.source === cur && !seen.has(e.target)) {
        seen.add(e.target);
        queue.push(e.target);
      }
    });
  }
  return seen;
};

const isInAiAgentBranch = (nodeId: any, nodes: any[], edges: any[]) => {
  const seen = new Set([nodeId]);
  const queue = [nodeId];
  while (queue.length) {
    const cur = queue.shift();
    if (nodes.find((n: any) => n.id === cur)?.type === 'aiAgentNode') return true;
    edges.forEach((e: any) => {
      if (e.target === cur && !seen.has(e.source)) {
        seen.add(e.source);
        queue.push(e.source);
      }
    });
  }
  return false;
};

export const aiBranchConnectError = (
  source: any,
  target: any,
  nodes: any[],
  edges: any[],
) => {
  if (!isInAiAgentBranch(source, nodes, edges)) return null;
  for (const id of collectDownstreamIds([target], edges)) {
    const type = nodes.find((n: any) => n.id === id)?.type;
    if (!AI_BRANCH_ALLOWED_TYPES.includes(type)) return AI_BRANCH_ERROR;
  }
  return null;
};

export const buildIvrAiCompletionType = (
  node: any,
  nodesArray: any[],
  edges: any[],
) =>
  (node.data?.completionTypes ?? []).map((ct: any) => {
    const handleId = aiCompletionHandleId(node.id, ct.name);
    const plays: any[] = [];
    let terminal: any = null;
    let edge = edges.find(
      (e: any) => e.source === node.id && e.sourceHandle === handleId,
    );
    const seen = new Set();
    while (edge && !seen.has(edge.target)) {
      seen.add(edge.target);
      const next = nodesArray.find((n: any) => n.id === edge.target);
      if (!next) break;
      if (next.type !== 'playNode') {
        terminal = next;
        break;
      }
      plays.push(next);
      edge = edges.find((e: any) => e.source === next.id);
    }

    const withMedia = plays.length > 0;
    const isTransfer = terminal?.type === 'transferNode';
    const rule: any = {
      name: ct.name,
      description: ct.description || '',
      event_type: isTransfer
        ? withMedia ? 'transfer_with_media' : 'transfer'
        : withMedia ? 'hangup_with_media' : 'hangup',
    };
    if (withMedia) {
      const play = plays[0].data || {};
      if (play.playType === 'tts') {
        rule.tts_text = play.ttsText || '';
      } else {
        rule.media_id = Number(play.action?.id);
        rule.media_language = play.action?.language || '';
      }
    }
    if (isTransfer) {
      const slot = (terminal.data?.timeSlots || [])[0];
      rule.transfer_type =
        slot?.type === 'transfer_group'
          ? AI_TRANSFER_TARGET_TYPES.CALL_GROUP
          : AI_TRANSFER_TARGET_TYPES.AGENT;
      rule.transfer_id = String(slot?.id ?? '');
    }
    return rule;
  });

export const fetchIsBackendDev = async () => {
  try {
    const resp: any = await getApiCall(AGENT_CALL_CONNECT_LIST);
    return resp?.node_env === 'development';
  } catch {
    return false;
  }
};

export const downloadIvrFlowJson = ({
  nodes = [],
  edges = [],
  flowName,
  isGatherNode,
  scheduleData,
  userId,
  smsWebhookFlag,
  isMissed = false,
}: any) => {
  if (!nodes.length) {
    toast.error('No nodes to export');
    return;
  }
  let url: any;
  try {
    const data = transformNodes({
      edges,
      flowName,
      nodesArray: nodes,
      isGatherNode,
      scheduleData,
      userId,
      smsWebhookFlag,
      isMissed,
    });
    const payload = {
      flowName,
      userId,
      is_gather_node: isGatherNode,
      promptDuration: data.promptDuration,
      ivr_category: isMissed ? 'missed' : 'normal',
      scheduleData: scheduleData || {},
      nodeData: data,
      edgesData: edges,
      rawNodes: nodes,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = `${flowName || 'ivr-flow'}-flow.json`;
    a.href = url;
    a.click();
    toast.success('Flow JSON downloaded');
  } catch (err) {
    console.error(err);
    toast.error('JSON export failed');
  } finally {
    if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

// Clicking the icon triggers the nearest handle so node can be connected
export const triggerHandleConnect = (e: any) => {
  console.log('[triggerHandleConnect] called');
  e.stopPropagation();
  const nodeEl = e.currentTarget.closest('.react-flow__node');
  const handle =
    nodeEl?.querySelector('.react-flow__handle-left') ||
    nodeEl?.querySelector('.react-flow__handle-right') ||
    nodeEl?.querySelector('.react-flow__handle');
  console.log('[triggerHandleConnect] handle:', handle);
  if (!handle) return;
  const rect = handle.getBoundingClientRect();
  const opts = {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX: rect.left + rect.width / 2,
    clientY: rect.top + rect.height / 2,
  };
  handle.dispatchEvent(new MouseEvent('mousedown', opts));
  handle.dispatchEvent(new MouseEvent('mouseup', opts));
  handle.dispatchEvent(new MouseEvent('click', opts));
};

// Drop self-loops and back-edges (retry/repeat-menu style loops) before handing
// edges to dagre for Tidy Layout — dagre ranks a DAG, so a real cycle either
// throws or forces one branch into a long edge that cuts across the tree.
// Convergent edges (two branches landing on the same downstream node, e.g. a
// shared Hangup/Transfer) are kept since those don't create a cycle.
export const getAcyclicLayoutEdges = (nodesList: any[], edgesList: any[]) => {
  const adjacency: any = {};
  edgesList.forEach((edge) => {
    if (edge.source === edge.target) return;
    if (!adjacency[edge.source]) adjacency[edge.source] = [];
    adjacency[edge.source].push(edge);
  });

  const state: any = {}; // undefined = unvisited, 1 = on the current DFS stack, 2 = finished
  const acyclicEdges: any[] = [];

  const visit = (nodeId: any) => {
    state[nodeId] = 1;
    (adjacency[nodeId] || []).forEach((edge: any) => {
      const targetState = state[edge.target];
      if (targetState === 1) return; // back-edge: would close a cycle, skip for layout
      acyclicEdges.push(edge);
      if (!targetState) visit(edge.target);
    });
    state[nodeId] = 2;
  };

  nodesList.forEach((node) => {
    if (!state[node.id]) visit(node.id);
  });

  return acyclicEdges;
};

// Walk the edge chain upward from nodeId to find the nearest LanguageNode ancestor
export const findUpstreamLanguageNode = (
  nodeId: any,
  getNodes: any,
  getEdges: any,
) => {
  const edges = getEdges();
  const nodes = getNodes();
  let currentId = nodeId;
  const visited = new Set();
  while (true) {
    if (visited.has(currentId)) break;
    visited.add(currentId);
    const inEdge = edges.find((e: any) => e.target === currentId);
    if (!inEdge) break;
    const upstream = nodes.find((n: any) => n.id === inEdge.source);
    if (!upstream) break;
    if (upstream.type === 'languageNode') return upstream;
    if (upstream.type === 'answerNode') break;
    currentId = inEdge.source;
  }
  return null;
};

const getDtmfIndex = (edge: any) => {
  const key = edge.sourceHandle?.split('-').pop();
  const idx = DTMF_KEYS.indexOf(key);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

// Recursive function to calculate left-most path duration
const calculateLeftPathDuration = (
  node: any,
  nodesArray: any[],
  edges: any[],
  visited = new Set(),
): number => {
  if (!node) return 0;

  // Always add the node's own duration first
  let duration = 0;
  if (node.data?.verb === 'play') {
    duration += node.data?.action?.duration || 0;
  } else if (node.data?.verb === 'record') {
    duration += node.data?.action?.duration || 0;
  } else if (node.data?.verb === 'dtmf' || node.data?.verb === 'gather') {
    duration += Number(node.data?.timeout ?? 0);
    duration += node.data?.action?.menu?.duration ?? 0;
  }

  // Avoid infinite loops by marking visited AFTER counting current node
  if (visited.has(node.id)) return duration;
  visited.add(node.id);

  const edgeData = edges.filter((e) => e.source === node.id);

  if (node.data?.verb === 'dtmf') {
    const sortedEdgeData = [...edgeData].sort(
      (a, b) => getDtmfIndex(a) - getDtmfIndex(b),
    );
    if (sortedEdgeData.length > 0) {
      const firstEdge = sortedEdgeData[0];
      const targetNode = nodesArray.find((n) => n.id === firstEdge.target);
      duration += calculateLeftPathDuration(
        targetNode,
        nodesArray,
        edges,
        visited,
      );
    }
  } else if (edgeData.length > 0) {
    const firstEdge = edgeData[0];
    const targetNode = nodesArray.find((n) => n.id === firstEdge.target);
    duration += calculateLeftPathDuration(
      targetNode,
      nodesArray,
      edges,
      visited,
    );
  }

  return duration;
};

export const transformNodes = ({
  nodesArray,
  edges,
  flowName,
  scheduleData,
  isGatherNode,
  userId,
  smsWebhookFlag,
  isMissed = false,
}: any) => {
  const cid = userId;
  const nodeIdMap = new Map(
    nodesArray.map((node: any, index: number) => [node.id, index]),
  );

  // Start from the first node in nodesArray (assume it's root)
  const totalDuration = calculateLeftPathDuration(
    nodesArray[0],
    nodesArray,
    edges,
  );
  const dtmfTargetNodeIds = new Set();

  const transformedNodes = nodesArray.reduce(
    (acc: any, node: any, index: number) => {
      const edgeData = edges.filter((obj: any) => obj.source === node.id);
      const nodeId = nodeIdMap.get(node.id);

      const flowJson: any = { verb: node.data?.verb };

      if (
        node.data?.verb === 'answer' ||
        node.data?.verb === 'hangup' ||
        node.data?.verb === 'dtmf' ||
        node.data?.verb === 'gather'
      ) {
        flowJson.webhookConfig = node.data?.webhookConfig ?? null;
      }

      if (node.data?.verb === 'play') {
        flowJson.playType = node.data?.playType || 'recorded';
        flowJson.action = node.data?.action;
        flowJson.duration = node.data?.action?.duration || 0;
        if (node.data?.playType === 'tts') {
          flowJson.ttsText = node.data?.ttsText || '';
          flowJson.usedVariables = node.data?.usedVariables || [];
          flowJson.usedFlowVariables = node.data?.usedFlowVariables || [];
        }
      }
      if (node.data?.verb === 'record') {
        flowJson.action = node.data?.action;
        flowJson.duration = node.data?.action?.duration || 0;
        flowJson.record_duration = node.data?.record_duration;
        flowJson.recordSilenceStop = node.data?.recordSilenceStop ?? 0;
        flowJson.recordStopDtmf = node.data?.recordStopDtmf ?? 'any_dtmf';
      } else if (
        node.data?.verb === 'dtmf' ||
        node.data?.verb === 'gather'
      ) {
        flowJson.type = node.data?.type;
        flowJson.action = node.data?.action;
        flowJson.numDigits = node.data?.numDigits;
        flowJson.timeout = node.data?.timeout;
        flowJson.numSources = node.data?.numSources;
        flowJson.rePrompt = node.data?.rePrompt;
        flowJson.ttsRows = node.data?.ttsRows ?? [];
        flowJson.minValue = node.data?.minValue ?? '';
        flowJson.smsData = node.data?.smsData ?? {};
        flowJson.manualDigits = node.data?.manualDigits ?? '';
        flowJson.makeAvailable = node.data?.passToNextNode || false;
        flowJson.variableName = node.data?.variableName || '';
        flowJson.availableVariables = node.data?.availableVariables || [];
      } else if (node.data?.verb === 'transfer') {
        flowJson.action = node.data?.action;
        flowJson.timeSlots = node.data?.timeSlots ?? [];
        flowJson.defaultAction = node.data?.defaultAction ?? null;
      } else if (node.data?.verb === 'default') {
        flowJson.mode = node.data?.mode || '';
        flowJson.action = node.data?.action ?? {};
        flowJson.max_duration = node.data?.max_duration ?? null;
        flowJson.use_media = !!node.data?.use_media;
        flowJson.parentNodeId = node.data?.parentNodeId ?? null;
      } else if (node.data?.verb === 'hangup') {
        flowJson.mode = node.data?.mode || 'hangup';
        flowJson.action = node.data?.action ?? {};
        flowJson.max_duration = node.data?.max_duration ?? null;
      } else if (node.data?.verb === 'voicemail') {
        flowJson.action = node.data?.action ?? {};
        flowJson.max_duration = node.data?.max_duration ?? null;
        flowJson.use_media = !!node.data?.use_media;
        flowJson.duration = node.data?.action?.duration ?? 0;
        flowJson.recordSilenceStop = node.data?.recordSilenceStop ?? 0;
        flowJson.recordStopDtmf = node.data?.recordStopDtmf ?? 'any_dtmf';
      } else if (node.data?.verb === 'missed') {
        flowJson.mode = node.data?.mode || '';
        flowJson.action = node.data?.action ?? {};
        flowJson.max_duration = node.data?.max_duration ?? null;
      } else if (node.data?.verb === 'api') {
        const apiAction = node.data?.action ?? {};
        flowJson.apiWebhookId = apiAction.webhook_id ?? null;
        flowJson.selectedFixedJsonKeys =
          apiAction.selected_fixed_json_keys ?? {};
        flowJson.selectedResponseParams =
          apiAction.selected_response_params ?? {};
        flowJson.action = apiAction;
      } else if (node.data?.verb === 'conditional') {
        flowJson.conditions = normalizeConditionGroups(
          node.data?.conditions,
        ).map((group: any, gi: number) => ({
          ...group,
          branch: conditionBranchKey(gi),
        }));
      } else if (node.data?.verb === 'ai_agent') {
        flowJson.aiAgentId = node.data?.aiAgentId ?? null;
        flowJson.aiAgentUuid = node.data?.aiAgentUuid ?? null;
        flowJson.aiAgentName = node.data?.aiAgentName ?? '';
        flowJson.completionTypes = node.data?.completionTypes ?? [];
        flowJson.completion_type = buildIvrAiCompletionType(
          node,
          nodesArray,
          edges,
        );
      }

      let flowControl: any = {};

      if (
        node.data?.verb === 'dtmf' &&
        Object.keys(node.data?.selectedHandles ?? {}).length !== 0
      ) {
        const sortedEdgeData = [...edgeData].sort(
          (a, b) => getDtmfIndex(a) - getDtmfIndex(b),
        );
        sortedEdgeData.forEach((edge: any) => {
          const key = edge.sourceHandle?.split('-').pop();
          const targetNumericId = nodeIdMap.get(edge.target);
          const targetNode = nodesArray.find((n: any) => n.id === edge.target);

          if (
            targetNode.data?.verb === 'play' ||
            targetNode.data?.verb === 'record'
          )
            dtmfTargetNodeIds.add(targetNode.id);
          if (!key || targetNumericId === undefined) return;

          flowControl[`dtmf_${key}`] = {
            flowName,
            nextNodeId: targetNumericId,
            isLoop: edge.source === edge.target,
            parent: node.data?.parent ?? '',
          };
        });
      } else if (node.data?.verb === 'conditional') {
        const branchTarget = (handleIds: any[]) => {
          const edge = edgeData.find((e: any) =>
            handleIds.includes(e.sourceHandle),
          );
          return edge ? nodeIdMap.get(edge.target) : undefined;
        };
        const addBranch = (key: string, targetNumericId: any) => {
          if (targetNumericId === undefined) return;
          flowControl[key] = {
            flowName,
            nextNodeId: targetNumericId,
            parent: node.data?.parent ?? '',
          };
        };
        normalizeConditionGroups(node.data?.conditions).forEach(
          (group: any, gi: number) =>
            addBranch(
              conditionBranchKey(gi),
              branchTarget(conditionGroupHandleIds(node.id, group.groupId, gi)),
            ),
        );
        addBranch('else', branchTarget([conditionElseHandleId(node.id)]));
      } else if (node.data?.verb === 'ai_agent') {
        (node.data?.completionTypes ?? []).forEach((ct: any) => {
          const handleId = aiCompletionHandleId(node.id, ct.name);
          const edge = edgeData.find((e: any) => e.sourceHandle === handleId);
          const targetNumericId = edge ? nodeIdMap.get(edge.target) : undefined;
          if (targetNumericId === undefined) return;

          flowControl[`completion_${ct.name}`] = {
            flowName,
            nextNodeId: targetNumericId,
            parent: node.data?.parent ?? '',
          };
        });
      } else if (edgeData.length > 0) {
        const targetNumericId = nodeIdMap.get(edgeData[0].target);
        if (targetNumericId) {
          flowControl.default = {
            flowName,
            nextNodeId: targetNumericId,
            parent: node.data?.parent ?? '',
          };
        }
      }

      acc[nodeId] = {
        id: nodeId,
        flowJson,
        flowRender: { ...node },
        flowControl,
      };

      return acc;
    },
    {},
  );

  return {
    flowName,
    userId: cid,
    nodes: transformedNodes,
    scheduleData,
    isGatherNode,
    promptDuration: totalDuration,
    smsWebhookFlag,
    ivr_category: isMissed ? 'missed' : 'normal',
  };
};

const computeTransferCoverage = (slots: any[] = []) => {
  return slots.reduce((acc: number, s: any) => {
    const start = s.start_time || s.startTime;
    const end = s.end_time || s.endTime;
    if (!start || !end || !start.includes(':') || !end.includes(':'))
      return acc;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const st = sh * 60 + sm;
    let en = eh * 60 + em;
    if (en <= st) en += 1440;
    acc += en - st;
    return acc;
  }, 0);
};

export const validateIVR = ({
  nodes = [],
  edges = [],
  flowName,
  scheduleData,
  isMissed = false,
  onInvalidNode,
}: any) => {
  if (!nodes.length) {
    toast.warning('Add at least one node.');
    return false;
  }

  if (!flowName) {
    toast.warning('IVR Name required.');
    return false;
  }

  if (Object.keys(scheduleData).length <= 0) {
    toast.warning('Time Schedule required.');
    return false;
  }

  const selectedDays = Array.isArray(scheduleData?.week_days)
    ? scheduleData.week_days
    : [];
  if (!selectedDays.length) {
    toast.warning('Please select at least one day in Time Schedule');
    return false;
  }

  const start = scheduleData?.start_time;
  const end = scheduleData?.end_time;
  const toMinutes = (timeValue: any) => {
    if (!timeValue || !String(timeValue).includes(':')) return NaN;
    const [hours, minutes] = String(timeValue).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  };

  const startMins = toMinutes(start);
  const endMins = toMinutes(end);
  if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
    toast.warning('Invalid time selected in Time Schedule');
    return false;
  }

  if (endMins <= startMins) {
    toast.warning('End time should be greater than start time');
    return false;
  }

  // 🔹 Build edge maps for O(1) lookups
  const incomingMap = new Map();
  const outgoingMap = new Map();

  for (const edge of edges) {
    if (!incomingMap.has(edge.target)) {
      incomingMap.set(edge.target, []);
    }
    if (!outgoingMap.has(edge.source)) {
      outgoingMap.set(edge.source, []);
    }

    incomingMap.get(edge.target).push(edge);
    outgoingMap.get(edge.source).push(edge);
  }

  const FULL_DAY_MINS = 1439;

  if (isMissed) {
    const allowed = new Set(['missedNode']);
    for (const node of nodes) {
      const nodeValid = (() => {
        const lbl = String(node?.data?.label ?? '').toUpperCase();
        if (!allowed.has(node.type)) {
          toast.warning(`Missed IVR can only contain Missed nodes`);
          return false;
        }
        const mode = node?.data?.mode;
        if (!mode) {
          toast.warning(`Missed node "${lbl}" must have an action selected`);
          return false;
        }
        if (mode === 'hangup_with_media' || mode === 'voicemail') {
          if (
            node?.data?.action?.prompt_category &&
            !node?.data?.action?.id
          ) {
            toast.warning(
              `Missed node "${lbl}" audio required when category selected`,
            );
            return false;
          }
        }
        if (mode === 'voicemail') {
          const dur = Number(node?.data?.max_duration);
          if (!dur || dur < 1 || dur > 60) {
            toast.warning(`Missed node "${lbl}" voicemail duration 1-60s`);
            return false;
          }
        }
        if (mode === 'webhook' && !node?.data?.action?.webhook_id) {
          toast.warning(`Missed node "${lbl}" must select a webhook`);
          return false;
        }
        if (mode === 'sms_webhook' && !node?.data?.action?.sms_webhook_id) {
          toast.warning(`Missed node "${lbl}" must select an SMS webhook`);
          return false;
        }
        if (mode === 'set_callback') {
          const cb = node?.data?.action || {};
          if (
            !cb.callback_delay ||
            (cb.retry_enabled && (!cb.retry_count || !cb.retry_interval)) ||
            (cb.did_mode === 'other' && !cb.did_id) ||
            ((cb.did_mode !== 'other' || cb.did_category === 'free') &&
              (!cb.routing?.action || !cb.routing?.id))
          ) {
            toast.warning(
              `Missed node "${lbl}" callback settings are incomplete`,
            );
            return false;
          }
        }
        return true;
      })();

      if (!nodeValid) {
        onInvalidNode?.(node.id);
        return false;
      }
    }
    return true;
  }

  for (const node of nodes) {
    const nodeValid = (() => {
      const incoming = incomingMap.get(node.id) || [];
      const outgoing = outgoingMap.get(node.id) || [];
      const label = String(node?.data?.label ?? '').toUpperCase();

      /* Rule 1: End nodes must be Hangup nodes (transfer + defaultNode exempt) */
      if (
        incoming.length === 0 &&
        outgoing.length === 0 &&
        node.type !== 'hangupNode' &&
        node.type !== 'defaultNode' &&
        node.type !== 'transferNode'
      ) {
        toast.warning(
          `Source and Handle of "${label}" must be a Connected to a node`,
        );
        return false;
      }

      /* Rule 2: Input handle must have at least one connection (except answer nodes) */
      if (node.type !== 'answerNode' && incoming.length === 0) {
        toast.warning(`Node "${label}" must have at least one input connected`);
        return false;
      }

      /* Rule 3: Selected output handles must be connected */
      const selectedHandles = node.data?.selectedHandles;
      if (selectedHandles) {
        for (const [handleId, handleObj] of Object.entries(selectedHandles)) {
          if (!handleObj || Object.keys(handleObj).length === 0) continue;

          const isConnected = outgoing.some(
            (edge: any) => edge.sourceHandle === handleId,
          );

          if (!isConnected) {
            const dtmf = handleId.split('-')[1];
            toast.warning(
              `Output handle "DTMF_${dtmf}" of node "${label}" must be connected`,
            );
            return false;
          }
        }
      }

      /* Conditional node validation: at least one condition, every row filled, both If/Else branches connected */
      if (node.type === 'conditionalNode') {
        const groups = normalizeConditionGroups(node?.data?.conditions);
        if (groups.length === 0) {
          toast.warning(
            `Conditional node "${label}" must have at least one condition`,
          );
          return false;
        }
        if (groups.length > MAX_CONDITIONS) {
          toast.warning(
            `Conditional node "${label}" can have at most ${MAX_CONDITIONS} conditions`,
          );
          return false;
        }
        for (let i = 0; i < groups.length; i++) {
          const rules = groups[i].rules;
          if (rules.length === 0 || rules.length > MAX_CONDITIONS) {
            toast.warning(
              `Conditional node "${label}" — condition ${
                i + 1
              } must have 1 to ${MAX_CONDITIONS} rules`,
            );
            return false;
          }
          if (!rules.every(isConditionRuleComplete)) {
            toast.warning(
              `Conditional node "${label}" — condition ${
                i + 1
              } is incomplete`,
            );
            return false;
          }
        }
        for (let i = 0; i < groups.length; i++) {
          const handleIds = conditionGroupHandleIds(
            node.id,
            groups[i].groupId,
            i,
          );
          if (
            !outgoing.some((edge: any) => handleIds.includes(edge.sourceHandle))
          ) {
            toast.warning(
              `Conditional node "${label}" — the "${conditionBranchLabel(
                i,
              )}" output must be connected`,
            );
            return false;
          }
        }
        const elseConnected = outgoing.some(
          (edge: any) => edge.sourceHandle === conditionElseHandleId(node.id),
        );
        if (!elseConnected) {
          toast.warning(
            `Conditional node "${label}" — the "Else" output must be connected`,
          );
          return false;
        }
      }

      if (node.type === 'aiAgentNode') {
        if (!node?.data?.aiAgentId) {
          toast.warning(`AI Agent node "${label}" must have an AI agent selected`);
          return false;
        }
        const completionTypes = node?.data?.completionTypes || [];
        if (completionTypes.length === 0) {
          toast.warning(
            `AI Agent node "${label}" has no completion types — pick another agent`,
          );
          return false;
        }
        for (const ct of completionTypes) {
          const handleId = aiCompletionHandleId(node.id, ct.name);
          if (!outgoing.some((edge: any) => edge.sourceHandle === handleId)) {
            toast.warning(
              `AI Agent node "${label}" — the "${ct.name}" output must be connected`,
            );
            return false;
          }
        }
        const branchIds = collectDownstreamIds(
          outgoing.map((edge: any) => edge.target),
          edges,
        );
        for (const branchId of branchIds) {
          const branchNode = nodes.find((n: any) => n.id === branchId);
          if (!AI_BRANCH_ALLOWED_TYPES.includes(branchNode?.type)) {
            toast.warning(
              `AI Agent node "${label}" — ${AI_BRANCH_ERROR} (found "${String(
                branchNode?.data?.label ?? branchId,
              ).toUpperCase()}")`,
            );
            return false;
          }
        }
      }

      /* Rule 4: End nodes must be Hangup/Voicemail/Transfer/Default nodes */
      if (
        outgoing.length === 0 &&
        node.type !== 'hangupNode' &&
        node.type !== 'voicemailNode' &&
        node.type !== 'defaultNode' &&
        node.type !== 'transferNode'
      ) {
        toast.warning(
          `End node "${label}" must be a Hangup, Voicemail, Transfer, or Default node`,
        );
        return false;
      }

      /* Voicemail node validation */
      if (node.type === 'voicemailNode') {
        if (
          node?.data?.use_media &&
          node?.data?.action?.prompt_category &&
          !node?.data?.action?.id
        ) {
          toast.warning(
            `Voicemail "${label}" audio required when category selected`,
          );
          return false;
        }
        const dur = Number(node?.data?.max_duration);
        if (!dur || dur < 5 || dur > 60) {
          toast.warning(`Voicemail "${label}" must have duration 5-60s`);
          return false;
        }
      }

      /* Transfer node: if <24h coverage, must have inline default action */
      if (node.type === 'transferNode') {
        const slots = node?.data?.timeSlots || [];
        if (slots.length === 0) {
          toast.warning(
            `Transfer node "${label}" must have at least one time slot`,
          );
          return false;
        }
        const cov = computeTransferCoverage(slots);
        if (cov < FULL_DAY_MINS) {
          const def = node?.data?.defaultAction;
          if (!def || !def.mode) {
            toast.warning(
              `Transfer "${label}" requires a default action (open settings and configure)`,
            );
            return false;
          }
          if (def.mode === 'hangup_with_media') {
            if (def.action?.prompt_category && !def.action?.id) {
              toast.warning(
                `Transfer "${label}" default audio required when category selected`,
              );
              return false;
            }
          }
          if (def.mode === 'voicemail') {
            if (
              def.use_media &&
              def.action?.prompt_category &&
              !def.action?.id
            ) {
              toast.warning(
                `Transfer "${label}" default audio required when category selected`,
              );
              return false;
            }
            const dur = Number(def.max_duration);
            if (!dur || dur < 5 || dur > 60) {
              toast.warning(
                `Transfer "${label}" default voicemail duration 5-60s`,
              );
              return false;
            }
          }
        }
      }

      /* Default node validation */
      if (node.type === 'defaultNode') {
        const mode = node?.data?.mode;
        if (!mode) {
          toast.warning(
            `Default node attached to "${
              node?.data?.parent || ''
            }" must have an action`,
          );
          return false;
        }
        if (mode === 'hangup_with_media') {
          if (
            node?.data?.action?.prompt_category &&
            !node?.data?.action?.id
          ) {
            toast.warning(
              `Default node audio required when category selected`,
            );
            return false;
          }
        }
        if (mode === 'voicemail') {
          if (
            node?.data?.use_media &&
            node?.data?.action?.prompt_category &&
            !node?.data?.action?.id
          ) {
            toast.warning(
              `Default node audio required when category selected`,
            );
            return false;
          }
          const dur = Number(node?.data?.max_duration);
          if (!dur || dur < 5 || dur > 60) {
            toast.warning(
              `Default node voicemail must have duration 5-60s`,
            );
            return false;
          }
        }
      }

      /* API node validations */
      if (node.type === 'apiNode') {
        if (!node?.data?.action?.webhook_id) {
          toast.warning(`Node "${label}" must have an API webhook selected`);
          return false;
        }
        const isMakeAvailable = node?.data?.action?.play_in_next_node || false;
        if (!isMakeAvailable) {
          const fixedKeys = node?.data?.action?.selected_fixed_json_keys || {};
          const responseParams =
            node?.data?.action?.selected_response_params || {};
          if (Object.keys(fixedKeys).length === 0) {
            toast.warning(
              `Node "${label}" must have at least one Fixed JSON parameter selected`,
            );
            return false;
          }
          if (Object.keys(responseParams).length === 0) {
            toast.warning(
              `Node "${label}" must have at least one Response Parameter selected`,
            );
            return false;
          }
        }
      }

      /* Rule 5: Play node must have audio (skip if TTS mode or ttsText present) */
      const playHasAudio =
        node?.data?.action?.audio ||
        (Array.isArray(node?.data?.action?.languages) &&
          node.data.action.languages.length > 0);
      if (
        node.type === 'playNode' &&
        node?.data?.playType !== 'tts' &&
        !node?.data?.ttsText &&
        !playHasAudio
      ) {
        toast.warning(`Node "${label}" must select an audio file`);
        return false;
      }

      /* Rule 6: Record node must have audio ad record_duration*/
      if (node.type === 'recordNode') {
        const action = node?.data?.action || {};
        const duration = node?.data?.record_duration ?? action?.duration;

        if (!action?.audio) {
          toast.error(`Node "${label}" must select an audio file`);
          return false;
        }

        if (!duration || Number(duration) <= 0) {
          toast.error(
            `Node "${label}" must have a valid record duration (positive integer)`,
          );
          return false;
        }
      }

      /* Rule 7: Gather node must have menu audio */
      const gatherMenuHasAudio =
        node?.data?.action?.menu?.audio ||
        (Array.isArray(node?.data?.action?.menu?.languages) &&
          node.data.action.menu.languages.length > 0);
      if (node.type === 'gatherNode' && !gatherMenuHasAudio) {
        toast.warning(`Node "${label}" must select an audio file`);
        return false;
      }

      return true;
    })();

    if (!nodeValid) {
      onInvalidNode?.(node.id);
      return false;
    }
  }

  return true;
};
